import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import Language from "@/domain/enums/Language";
import useGetDateFnsLocale from "@/hooks/utils/useGetDateFnsLocale";
import i18n from "@/i18n";
import { cn } from "@/lib/utils";
import { CalendarIcon, X } from "lucide-react";

type CalendarPickerProps = {
    date?: Date;
    setDate: (date: Date | undefined) => void;
    label: string;
    placeholder: string;
};

const CalendarPicker = ({
    date,
    setDate,
    label,
    placeholder,
}: CalendarPickerProps) => {
    const { language } = i18n;
    const locale = useGetDateFnsLocale(language as Language);

    return (
        <div className="flex items-center gap-1">
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        className={cn(
                            "h-auto bg-surface-activeFg px-2 py-1 text-xs font-normal text-foreground [&_svg]:size-3",
                            !date && "text-muted-foreground",
                        )}
                        variant="secondary"
                    >
                        {date ? (
                            <span>
                                {label} {date.toLocaleDateString()}
                            </span>
                        ) : (
                            <span>{placeholder}</span>
                        )}
                        <CalendarIcon className="ml-auto opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                    <Calendar
                        locale={locale}
                        mode="single"
                        onSelect={setDate}
                        selected={date}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
            {date && (
                <Button
                    className="self-stretch h-full px-1 text-muted-foreground hover:text-red-500 hover:bg-red-50 [&_svg]:size-3"
                    onClick={() => setDate(undefined)}
                    size="icon"
                    type="button"
                    variant="ghost"
                >
                    <X />
                </Button>
            )}
        </div>
    );
};

export default CalendarPicker;
